/**
 * Permission Service
 *
 * API client for permission management endpoints.
 * Handles communication with the backend for permission operations.
 * All permissions use product:action format (e.g. "customers:view").
 */

import type { PermissionName } from '@/constants/permissions';
import type { UserRole } from '../types/auth.types';
import { tokenService } from './tokenService';
import { GATEWAY_BASE_URL } from '../config/gateway';

// Use Web Gateway for all API calls
const API_BASE_URL = `${GATEWAY_BASE_URL}/api/v1`;

// ─── Types ───────────────────────────────────────────────────────────────────

/** Map of permission string -> granted/denied boolean */
export interface PermissionSet {
    [permission: string]: boolean;
}

export interface UserPermissionsResponse {
    user_id: string;
    institution_id: string;
    role: string;
    permissions: string[];
}

/** Server-resolved role and permissions for one selected institution workspace. */
export interface ResolvedInstitutionPermissions {
    userId: string;
    institutionId: string;
    role: UserRole;
    permissions: PermissionSet;
}

export interface RolePermissionsResponse {
    role: string;
    permissions: string[];
}

export interface PermissionOverride {
    id: string;
    user_id: string;
    institution_id: string;
    permission_name: string;
    override_type: 'grant' | 'revoke';
    reason: string;
    valid_from?: string | null;
    valid_until: string | null;
    created_at: string;
    updated_at: string;
}

export interface CreateOverrideRequest {
    institution_id: string;
    permission_name: string;
    override_type: 'grant' | 'revoke';
    reason: string;
    valid_until?: string | null;
}

export interface BulkPermissionOverrideRequest {
    institution_id: string;
    permissions: PermissionName[];
    reason: string;
    valid_until?: string | null;
}

export type BulkGrantPermissionsRequest = BulkPermissionOverrideRequest;
export type BulkRevokePermissionsRequest = BulkPermissionOverrideRequest;

export interface BulkGrantPermissionsResponse {
    user_id: string;
    institution_id: string;
    permissions: string[];
    overrides_created: number;
    overrides_removed: number;
}

export interface Permission {
    name: string;
    product: string;
    action: string;
    description: string;
}

export interface AllPermissionsResponse {
    total: number;
    permissions: Permission[];
}

// ─── Internal helpers ────────────────────────────────────────────────────────

const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object';

const isNonEmptyString = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

const validationFieldMessage = (fields: unknown): string | undefined => {
    if (!isRecord(fields)) return undefined;

    for (const [field, entries] of Object.entries(fields)) {
        if (!Array.isArray(entries)) continue;

        for (const entry of entries) {
            if (!isRecord(entry)) continue;
            if (isNonEmptyString(entry.message)) return entry.message;
            if (isNonEmptyString(entry.code)) {
                return `${field}: ${entry.code.replaceAll('_', ' ')}`;
            }
        }
    }

    return undefined;
};

const apiErrorMessage = (payload: unknown): string | undefined => {
    if (isNonEmptyString(payload)) return payload;
    if (!isRecord(payload)) return undefined;

    if (isNonEmptyString(payload.message)) return payload.message;
    if (isRecord(payload.error) && isNonEmptyString(payload.error.message)) {
        return payload.error.message;
    }

    const fieldMessage = validationFieldMessage(payload.fields);
    if (fieldMessage) return fieldMessage;

    return isNonEmptyString(payload.error) ? payload.error : undefined;
};

const createHeaders = async (
    institutionId?: string,
    includeStoredInstitution = true,
): Promise<Record<string, string>> => {
    await tokenService.getAccessToken();

    const headers = institutionId
        ? tokenService.createAuthHeaders({ 'X-Institution-ID': institutionId })
        : tokenService.createAuthHeaders();

    if (!includeStoredInstitution) {
        delete headers['X-Institution-ID'];
        delete headers['x-institution-id'];
    }

    return headers;
};

const normalizePermissionSet = (permissions: unknown): PermissionSet => {
    if (!Array.isArray(permissions)) {
        throw new Error('Invalid permissions response');
    }

    return permissions.reduce<PermissionSet>((result, permission) => {
        if (typeof permission === 'string' && permission.length > 0) {
            result[permission] = true;
        }
        return result;
    }, {});
};

const VALID_USER_ROLES: readonly UserRole[] = [
    'platform-admin',
    'platform-support',
    'org-admin',
    'org-manager',
    'org-viewer',
];

const isUserRole = (value: unknown): value is UserRole =>
    typeof value === 'string' && VALID_USER_ROLES.includes(value as UserRole);

const normalizeResolvedInstitutionPermissions = (
    data: unknown,
    expectedUserId: string,
    expectedInstitutionId: string,
): ResolvedInstitutionPermissions => {
    if (!isRecord(data)) {
        throw new Error('Invalid resolved permissions response');
    }

    const { user_id, institution_id, role, permissions } = data;
    if (
        user_id !== expectedUserId ||
        institution_id !== expectedInstitutionId ||
        !isUserRole(role) ||
        !Array.isArray(permissions) ||
        permissions.some(
            (permission) =>
                typeof permission !== 'string' || permission.trim().length === 0,
        )
    ) {
        throw new Error('Invalid resolved permissions response');
    }

    return {
        userId: user_id,
        institutionId: institution_id,
        role,
        permissions: permissions.reduce<PermissionSet>((result, permission) => {
            result[permission] = true;
            return result;
        }, {}),
    };
};

async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const responseBody = await response.text();
        let payload: unknown;
        try {
            payload = JSON.parse(responseBody);
        } catch {
            payload = responseBody;
        }

        throw new Error(
            apiErrorMessage(payload)
            ?? `Permission request failed (HTTP ${response.status})`,
        );
    }
    return response.json();
}

const permissionSetFromResponse = (data: unknown): PermissionSet => {
    if (!isRecord(data)) {
        throw new Error('Invalid permissions response');
    }

    return normalizePermissionSet(data.permissions);
};

const normalizePermissionOverride = (value: unknown): PermissionOverride => {
    if (!isRecord(value)) {
        throw new Error('Invalid permission override response');
    }

    const {
        id,
        user_id,
        institution_id,
        permission_name,
        override_type,
        reason,
        valid_from,
        valid_until,
        created_at,
        updated_at,
    } = value;
    const normalizedValidUntil = typeof valid_until === 'string' ? valid_until : null;

    if (
        typeof id !== 'string' ||
        typeof user_id !== 'string' ||
        typeof institution_id !== 'string' ||
        typeof permission_name !== 'string' ||
        (override_type !== 'grant' && override_type !== 'revoke') ||
        typeof reason !== 'string' ||
        typeof created_at !== 'string' ||
        typeof updated_at !== 'string'
    ) {
        throw new Error('Invalid permission override response');
    }

    return {
        id,
        user_id,
        institution_id,
        permission_name,
        override_type,
        reason,
        valid_from: typeof valid_from === 'string' ? valid_from : null,
        valid_until: normalizedValidUntil,
        created_at,
        updated_at,
    };
};

const normalizeOverridesResponse = (data: unknown): PermissionOverride[] => {
    if (!isRecord(data)) {
        throw new Error('Invalid overrides response');
    }

    if (Array.isArray(data.overrides)) {
        return data.overrides.map(normalizePermissionOverride);
    }

    if (Array.isArray(data.data)) {
        return data.data.map(normalizePermissionOverride);
    }

    throw new Error('Invalid overrides response');
};

// ─── Service ─────────────────────────────────────────────────────────────────

export const permissionService = {
    /**
     * Resolve the authenticated user's server-derived role and permissions for
     * one explicitly selected institution workspace.
     */
    async resolveAuthenticatedUserPermissions(
        userId: string,
        institutionId: string,
    ): Promise<ResolvedInstitutionPermissions> {
        if (!userId.trim() || !institutionId.trim()) {
            throw new Error('User ID and institution ID are required');
        }

        const url = new URL(`${API_BASE_URL}/users/${userId}/permissions`);
        url.searchParams.append('institutionId', institutionId);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: await createHeaders(institutionId),
        });

        const data = await handleResponse<unknown>(response);
        return normalizeResolvedInstitutionPermissions(data, userId, institutionId);
    },

    /**
     * Get user's complete permissions (role defaults + overrides).
     */
    async getUserPermissions(
        userId: string,
        institutionId: string,
    ): Promise<PermissionSet> {
        const url = new URL(`${API_BASE_URL}/users/${userId}/permissions`);
        url.searchParams.append('institutionId', institutionId);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: await createHeaders(institutionId),
        });

        const data = await handleResponse<unknown>(response);
        return permissionSetFromResponse(data);
    },

    /**
     * Get default permissions for a role.
     */
    async getRolePermissions(
        role: string,
        options: { usePlatformContext?: boolean } = {},
    ): Promise<PermissionSet> {
        const response = await fetch(`${API_BASE_URL}/roles/${role}/permissions`, {
            method: 'GET',
            headers: await createHeaders(undefined, !options.usePlatformContext),
        });

        const data = await handleResponse<unknown>(response);
        return permissionSetFromResponse(data);
    },

    /**
     * Check if user has a specific permission.
     */
    async checkPermission(
        userId: string,
        institutionId: string,
        permission: PermissionName,
    ): Promise<boolean> {
        const url = new URL(`${API_BASE_URL}/users/${userId}/permissions/${permission}`);
        url.searchParams.append('institutionId', institutionId);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: await createHeaders(institutionId),
        });

        const data = await handleResponse<{ has_permission: boolean }>(response);
        return data.has_permission;
    },

    /**
     * List all permissions defined in the backend registry.
     */
    async getAllPermissions(): Promise<Permission[]> {
        const response = await fetch(`${API_BASE_URL}/permissions`, {
            method: 'GET',
            headers: await createHeaders(),
        });

        const data = await handleResponse<AllPermissionsResponse>(response);
        return data.permissions;
    },

    /**
     * Get user's permission overrides.
     */
    async getUserOverrides(userId: string, institutionId: string): Promise<PermissionOverride[]> {
        const url = new URL(`${API_BASE_URL}/users/${userId}/permissions/overrides`);
        url.searchParams.append('institutionId', institutionId);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: await createHeaders(institutionId),
        });

        const data = await handleResponse<unknown>(response);
        return normalizeOverridesResponse(data);
    },

    /**
     * Create a permission override for a user.
     */
    async createPermissionOverride(
        userId: string,
        override: CreateOverrideRequest,
    ): Promise<PermissionOverride> {
        const payload = {
            institutionId: override.institution_id,
            overrideType: override.override_type,
            permissionName: override.permission_name,
            reason: override.reason,
            validUntil: override.valid_until ?? null,
        };

        const response = await fetch(
            `${API_BASE_URL}/users/${userId}/permissions/overrides`,
            {
                method: 'POST',
                headers: await createHeaders(override.institution_id),
                body: JSON.stringify(payload),
            },
        );

        const data = await handleResponse<unknown>(response);
        if (isRecord(data) && 'data' in data) {
            return normalizePermissionOverride(data.data);
        }

        return normalizePermissionOverride(data);
    },

    /**
     * Atomically grant several permissions in one selected institution.
     */
    async bulkGrantPermissions(
        userId: string,
        request: BulkGrantPermissionsRequest,
    ): Promise<BulkGrantPermissionsResponse> {
        const response = await fetch(
            `${API_BASE_URL}/users/${userId}/permissions/grants`,
            {
                method: 'PUT',
                headers: await createHeaders(request.institution_id),
                body: JSON.stringify({
                    institutionId: request.institution_id,
                    permissions: request.permissions,
                    reason: request.reason,
                    validUntil: request.valid_until ?? null,
                }),
            },
        );

        return handleResponse<BulkGrantPermissionsResponse>(response);
    },

    /**
     * Atomically revoke several permissions in one selected institution.
     */
    async bulkRevokePermissions(
        userId: string,
        request: BulkRevokePermissionsRequest,
    ): Promise<BulkGrantPermissionsResponse> {
        const response = await fetch(
            `${API_BASE_URL}/users/${userId}/permissions/revokes`,
            {
                method: 'PUT',
                headers: await createHeaders(request.institution_id),
                body: JSON.stringify({
                    institutionId: request.institution_id,
                    permissions: request.permissions,
                    reason: request.reason,
                    validUntil: request.valid_until ?? null,
                }),
            },
        );

        return handleResponse<BulkGrantPermissionsResponse>(response);
    },

    /**
     * Delete a permission override.
     */
    async deletePermissionOverride(
        userId: string,
        overrideId: string,
        institutionId: string,
    ): Promise<void> {
        const response = await fetch(
            `${API_BASE_URL}/users/${userId}/permissions/overrides/${overrideId}`,
            {
                method: 'DELETE',
                headers: await createHeaders(institutionId),
            },
        );

        await handleResponse<unknown>(response);
    },

    /**
     * Bulk check multiple permissions.
     */
    async checkMultiplePermissions(
        userId: string,
        institutionId: string,
        permissions: PermissionName[],
    ): Promise<Record<PermissionName, boolean>> {
        const userPermissions = await this.getUserPermissions(userId, institutionId);

        const result: Record<string, boolean> = {};
        permissions.forEach((permission) => {
            result[permission] = userPermissions[permission] === true;
        });

        return result;
    },

    /**
     * Compare permissions between two roles.
     */
    async compareRolePermissions(role1: string, role2: string): Promise<{
        role1: PermissionSet;
        role2: PermissionSet;
        differences: string[];
    }> {
        const [perms1, perms2] = await Promise.all([
            this.getRolePermissions(role1),
            this.getRolePermissions(role2),
        ]);

        const differences: string[] = [];
        const allPermissions = new Set([
            ...Object.keys(perms1),
            ...Object.keys(perms2),
        ]);

        allPermissions.forEach((perm) => {
            if (perms1[perm] !== perms2[perm]) {
                differences.push(perm);
            }
        });

        return { role1: perms1, role2: perms2, differences };
    },
};
